import { useRef, useLayoutEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { X, Check, Folder, Clock } from 'lucide-react';

gsap.registerPlugin(ScrollTrigger);

export default function ProblemSolution({ className = '' }: { className?: string }) {
  const sectionRef = useRef<HTMLElement>(null);
  const problemRef = useRef<HTMLDivElement>(null);
  const solutionRef = useRef<HTMLDivElement>(null);
  const arrowRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const ctx = gsap.context(() => {
      const scrollTl = gsap.timeline({
        scrollTrigger: { trigger: section, start: 'top top', end: '+=70%', pin: true, scrub: 0.6 },
      });
      scrollTl.fromTo(problemRef.current, { x: '-30vw', opacity: 0 }, { x: 0, opacity: 1, ease: 'none' }, 0);
      scrollTl.fromTo(solutionRef.current, { x: '30vw', opacity: 0 }, { x: 0, opacity: 1, ease: 'none' }, 0.05);
      scrollTl.fromTo(arrowRef.current, { scale: 0, opacity: 0 }, { scale: 1, opacity: 1, ease: 'back.out(1.5)' }, 0.15);
      scrollTl.fromTo(problemRef.current, { x: 0, opacity: 1 }, { x: '-10vw', opacity: 0, ease: 'power2.in' }, 0.7);
      scrollTl.fromTo(solutionRef.current, { x: 0, opacity: 1 }, { x: '10vw', opacity: 0, ease: 'power2.in' }, 0.7);
      scrollTl.fromTo(arrowRef.current, { opacity: 1 }, { opacity: 0, ease: 'power2.in' }, 0.75);
    }, section);

    return () => ctx.revert();
  }, []);

  const painPoints = ['Hunting through folders for logos', 'Asking artists for assets again', 'Inconsistent branding across flyers', 'Wasting 30+ minutes per flyer'];
  const solutions = ['All assets organized in one place', 'Artist profiles with their logos ready', 'Consistent, on-brand AI generation', 'Generate flyers in seconds'];

  return (
    <section ref={sectionRef} className={`pinned-section bg-[#0B0F10] flex items-center ${className}`}>
      <div className="w-full px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <span className="font-mono text-xs uppercase tracking-[0.12em] text-[#FF6B4A] mb-4 block">The Problem</span>
            <h2 className="font-display font-bold text-[clamp(32px,4vw,56px)] leading-tight text-white">No more file hunting.</h2>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 relative">
            <div ref={problemRef} className="bg-[#1a1f21] rounded-2xl p-8 border border-[#FF6B4A]/30" style={{ willChange: 'transform, opacity' }}>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-[#FF6B4A]/20 flex items-center justify-center">
                  <Clock size={24} className="text-[#FF6B4A]" />
                </div>
                <h3 className="font-display font-semibold text-2xl text-white">The Old Way</h3>
              </div>
              <ul className="space-y-4">
                {painPoints.map((point, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <X size={20} className="text-[#FF6B4A] flex-shrink-0 mt-0.5" />
                    <span className="text-[#A9B4B8]">{point}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div ref={arrowRef} className="hidden lg:flex absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10" style={{ willChange: 'transform, opacity' }}>
              <div className="w-16 h-16 rounded-full bg-[#2EC4B6] flex items-center justify-center shadow-lg shadow-[#2EC4B6]/30">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" className="text-[#0B0F10]">
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </div>
            </div>
            <div ref={solutionRef} className="bg-[#122A2F] rounded-2xl p-8 border border-[#2EC4B6]/30" style={{ willChange: 'transform, opacity' }}>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-[#2EC4B6]/20 flex items-center justify-center">
                  <Folder size={24} className="text-[#2EC4B6]" />
                </div>
                <h3 className="font-display font-semibold text-2xl text-white">With Event.Tools</h3>
              </div>
              <ul className="space-y-4">
                {solutions.map((point, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <Check size={20} className="text-[#2EC4B6] flex-shrink-0 mt-0.5" />
                    <span className="text-[#A9B4B8]">{point}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
